import React from "react";
import { components, typography } from "../styles/designSystem";
import { serviceCategories } from "../data/servicesData";

const inputClasses =
  "w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder-white/50 transition-all duration-300 focus:border-white/40 focus:bg-white/15 focus:outline-none";

const FormField = ({ label, children }) => (
  <div>
    <label className={`${typography.label} mb-2 block text-white/70`}>
      {label}
    </label>

    {children}
  </div>
);

const StatusMessage = ({ status }) => {
  if (status === "success") {
    return (
      <div className="rounded-xl border border-green-400/50 bg-green-500/20 p-4">
        <p className="text-sm font-medium text-green-200">
          ✓ Message sent successfully! We'll be in touch soon.
        </p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="rounded-xl border border-red/50 bg-red/20 p-4">
        <p className="text-sm font-medium text-red">
          Something went wrong. Please try again or email us directly at
          admin@o3s.dev.
        </p>
      </div>
    );
  }

  return null;
};

const ContactForm = ({
                       formData,
                       status,
                       handleChange,
                       handleSubmit,
                       variant = "compact",
                     }) => {
  const isFullPage = variant === "full";

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 rounded-3xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl shadow-black/40 backdrop-blur-md transition-all duration-300 hover:border-white/20 md:p-10"
    >
      {isFullPage && (
        <p className={`${typography.label} mb-4 text-white/60`}>
          Tell us about yourself
        </p>
      )}

      <div className="grid gap-5 md:grid-cols-2">
        <FormField label="Your name">
          <input
            required
            name="name"
            type="text"
            autoComplete="name"
            value={formData.name}
            onChange={handleChange}
            className={inputClasses}
            placeholder="Your name"
          />
        </FormField>

        <FormField label="Email">
          <input
            required
            name="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            className={inputClasses}
            placeholder="you@example.com"
          />
        </FormField>
      </div>

      <FormField label="WhatsApp / phone number (optional)">
        <input
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={formData.phone}
          onChange={handleChange}
          className={inputClasses}
          placeholder="e.g., +49 123 456789"
        />

        <p className="mt-2 text-xs text-white/50">
          Add your phone number if you would prefer us to contact you there.
        </p>
      </FormField>

      <div className="grid gap-5 md:grid-cols-2">
        <FormField label="What do you need?">
          <select
            name="service"
            value={formData.service}
            onChange={handleChange}
            className={inputClasses}
          >
            <option value="" disabled className="text-gray-900">
              Select an option
            </option>

            {Object.entries(serviceCategories).map(([slug, label]) => (
              <option key={slug} value={slug} className="text-gray-900">
                {label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Budget (optional)">
          <input
            name="budget"
            type="text"
            value={formData.budget}
            onChange={handleChange}
            className={inputClasses}
            placeholder={isFullPage ? "e.g., €1k-€5k" : "Optional"}
          />
        </FormField>
      </div>

      <FormField label="How did you hear about us? (optional)">
        <select
          name="referralSource"
          value={formData.referralSource}
          onChange={handleChange}
          className={inputClasses}
        >
          <option value="" className="text-gray-900">
            Select an option
          </option>
          <option value="google" className="text-gray-900">
            Google
          </option>
          <option value="instagram" className="text-gray-900">
            Instagram
          </option>
          <option value="linkedin" className="text-gray-900">
            LinkedIn
          </option>
          <option value="referral" className="text-gray-900">
            Friend or colleague
          </option>
          <option value="previous-client" className="text-gray-900">
            Previous client
          </option>
          <option value="event" className="text-gray-900">
            Event or conference
          </option>
          <option value="other" className="text-gray-900">
            Other
          </option>
        </select>
      </FormField>

      <FormField label={isFullPage ? "Tell us about your project" : "Message"}>
        <textarea
          required
          name="message"
          rows={isFullPage ? 5 : 4}
          value={formData.message}
          onChange={handleChange}
          className={`${inputClasses} resize-none`}
          placeholder="Share your vision, goals, timeline, and what success looks like for you..."
        />
      </FormField>

      <StatusMessage status={status} />

      <div className={isFullPage ? "pt-4" : "pt-2"}>
        <button
          type="submit"
          disabled={status === "loading"}
          className={`${components.button.primary} ${
            isFullPage ? "w-full" : "w-full sm:w-auto"
          } justify-center disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {status === "loading" ? "Sending..." : "Send message"}

          {isFullPage && (
            <svg
              aria-hidden="true"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M13 7l5 5m0 0l-5 5m5-5H6"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          )}
        </button>
      </div>

      {isFullPage && (
        <p className="text-center text-xs text-white/60">
          We typically reply within{" "}
          <span className="font-semibold">1-2 business days</span>.
        </p>
      )}
    </form>
  );
};

export default ContactForm;
